import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { toast } from '@/utils/toast';
import api from '@/services/api';

export interface IndustrySettings {
  industries: string[];
}

/**
 * Fetch industry settings for a team
 */
export const useTeamIndustries = (teamId: number) => {
  return useQuery<IndustrySettings, AxiosError>({
    queryKey: ['industry-settings', teamId],
    queryFn: async () => {
      const response = await api.get(`/api/teams/${teamId}/industry-settings`);
      return response.data.data;
    },
    enabled: !!teamId && teamId > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Update industry settings for a team
 */
export const useUpdateTeamIndustries = () => {
  const queryClient = useQueryClient();

  return useMutation<
    IndustrySettings,
    AxiosError<{ error: string }>,
    { teamId: number; industries: string[] }
  >({
    mutationFn: async ({ teamId, industries }) => {
      const response = await api.patch(`/api/teams/${teamId}/industry-settings`, { industries });
      return response.data.data;
    },
    onSuccess: (_, variables) => {
      toast.success('Industry settings updated');
      queryClient.invalidateQueries({ queryKey: ['industry-settings', variables.teamId] });
      // Also invalidate trends since industries changed
      queryClient.invalidateQueries({ queryKey: ['dashboard-trends', variables.teamId] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to update industry settings');
    },
  });
};
